import * as React from 'react';
import { useUser } from '@clerk/clerk-react';

import type { DeskConversation, DeskStatusFilter } from './RxSupportDesk';

/**
 * Filter / selection / pagination state shared by the platform and partner
 * desk containers. Hook-free of tRPC so both scopes can plug their own
 * queries in.
 */
export function useRxSupportDeskState() {
  const { user } = useUser();
  const [filters, setFilters] = React.useState<{ status: DeskStatusFilter; storeId: string | null; search: string }>({
    status: 'open',
    storeId: null,
    search: '',
  });
  // `?c=<conversationId>` opens a thread directly (links in the Slack alerts).
  const [selectedId, setSelectedIdState] = React.useState<string | null>(() =>
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('c'),
  );
  const setSelectedId = React.useCallback((id: string | null) => {
    setSelectedIdState(id);
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (id) url.searchParams.set('c', id);
    else url.searchParams.delete('c');
    window.history.replaceState(window.history.state, '', url.toString());
  }, []);
  const [pages, setPages] = React.useState<DeskConversation[][]>([]);
  const [cursor, setCursor] = React.useState<string | null>(null);

  const onFiltersChange = React.useCallback((next: typeof filters) => {
    setFilters(next);
    setPages([]);
    setCursor(null);
  }, []);

  const listInput = React.useMemo(
    () => ({
      status: filters.status,
      storeId: filters.storeId ?? undefined,
      search: filters.search || undefined,
      cursor,
      limit: 50,
    }),
    [filters, cursor],
  );

  return {
    currentUserId: user?.id ?? null,
    filters,
    onFiltersChange,
    selectedId,
    setSelectedId,
    pages,
    setPages,
    cursor,
    setCursor,
    listInput,
  };
}

/** Merge the current page into the accumulated list (dedupe by id, newest first). */
export const mergePages = (pages: DeskConversation[][], current: DeskConversation[] | undefined) => {
  const seen = new Set<string>();
  const out: DeskConversation[] = [];
  for (const page of [...pages, current ?? []]) {
    for (const c of page) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(c);
    }
  }
  return out;
};
